/**
 * Reactive subscription to the current Firebase Auth ID-token custom
 * claims. Server-set via `refreshUserClaims` inside
 * `functions/src/userClaims.ts`. Shape:
 *
 *   claims.orgs         : Record<orgId, RoleKey>
 *   claims.primaryOrgId : string
 *
 * Mirrors the mobile hook `src/features/org/useTokenClaims.ts` so the
 * two clients agree on what "the user's roles are" with a single
 * source of truth (the auth token, not Firestore).
 *
 * Why a separate hook (vs. just reading the org doc):
 *   - The cached token is a synchronous read after first hydration
 *     — no Firestore round-trip. Org switching feels instant.
 *   - Encodes role per org, not just membership — usePermissions can
 *     resolve the active role without joining `organizations.roles`.
 *   - Survives "client" memberships (clients aren't in `memberIds`,
 *     so an org-doc query won't surface their orgs; the claim does).
 *
 * Refresh strategy:
 *   - `getIdTokenResult(false)` on every auth state change (cheap;
 *     cached token).
 *   - One forced refresh ~500ms after the first claims read to pick
 *     up claims that landed during sign-in (e.g. `claimInvites`
 *     wrote new memberships during a first login race).
 *   - `refresh(force=true)` exposed for org-switch code so the new
 *     `primaryOrgId` is visible synchronously after the callable.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { useAuth } from '@/auth/AuthProvider';

export type TokenClaims = {
  /** Map of orgId → user's role in that org. Empty before onboarding. */
  orgs: Record<string, string>;
  /** Active org id. Empty string when not set. */
  primaryOrgId: string;
};

const EMPTY: TokenClaims = { orgs: {}, primaryOrgId: '' };

export type UseTokenClaimsResult = {
  claims: TokenClaims;
  /** True until the first token read settles. */
  loading: boolean;
  /** Force-refresh the local token cache. Returns the new claims. */
  refresh: (force?: boolean) => Promise<TokenClaims>;
};

function parseClaims(raw: Record<string, unknown> | null | undefined): TokenClaims {
  if (!raw) return EMPTY;
  const orgsRaw = raw.orgs;
  const orgs: Record<string, string> =
    orgsRaw && typeof orgsRaw === 'object' && !Array.isArray(orgsRaw)
      ? Object.fromEntries(
          Object.entries(orgsRaw as Record<string, unknown>)
            .filter(([, v]) => typeof v === 'string')
            .map(([k, v]) => [k, v as string]),
        )
      : {};
  const primaryOrgId =
    typeof raw.primaryOrgId === 'string' ? (raw.primaryOrgId as string) : '';
  return { orgs, primaryOrgId };
}

export function useTokenClaims(): UseTokenClaimsResult {
  const { user, loading: authLoading } = useAuth();
  const [claims, setClaims] = useState<TokenClaims>(EMPTY);
  const [loading, setLoading] = useState(true);
  // Single-flight guard so simultaneous `refresh(true)` calls
  // collapse into one network round-trip.
  const inflightRef = useRef<Promise<TokenClaims> | null>(null);

  const refresh = useCallback(
    async (force = false): Promise<TokenClaims> => {
      if (!user) {
        setClaims(EMPTY);
        return EMPTY;
      }
      if (inflightRef.current) return inflightRef.current;
      const p = (async () => {
        const token = await user.getIdTokenResult(force);
        const next = parseClaims(token.claims as Record<string, unknown>);
        setClaims(next);
        return next;
      })();
      inflightRef.current = p;
      try {
        return await p;
      } finally {
        inflightRef.current = null;
      }
    },
    [user],
  );

  useEffect(() => {
    if (authLoading) {
      setLoading(true);
      return;
    }
    if (!user) {
      setClaims(EMPTY);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    // Cached read first (synchronous after hydration).
    user
      .getIdTokenResult(false)
      .then((token) => {
        if (cancelled) return;
        setClaims(parseClaims(token.claims as Record<string, unknown>));
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[useTokenClaims] cached read failed:', err);
        setLoading(false);
      });
    // One forced refresh shortly after to catch any claims that
    // landed during sign-in (claimInvites, fresh role grants, etc.)
    const t = setTimeout(() => {
      if (cancelled) return;
      void refresh(true).catch((err) => {
        console.warn('[useTokenClaims] forced refresh failed:', err);
      });
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [user, authLoading, refresh]);

  return { claims, loading, refresh };
}
